import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import {isLoggedInSelector} from 'modules/authentication/selectors';
import {applicationBySlugSelector} from 'modules/applications/selectors';
import {HOME_URL} from 'paths';
import colours from 'styles/colours';

/**
 * Generic breadcrumbs component that sits below the header bar.
 */

const styles = {
    breadcrumbs: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 4,
        fontSize: 12.5,
        color: colours.font.subtle,
        margin: 0,
        padding: '6px 16px',
        backgroundColor: colours.background.menuItemHover,
        borderBottom: `1px solid ${colours.border.dropdown}`,
        listStyle: 'none',
    },
    crumb: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    },
    link: {
        color: colours.font.link,
        textDecoration: 'none',
        fontWeight: 600,
        transition: 'all 0.15s',
    },
    linkHover: {
        color: colours.font.linkHover,
        textDecoration: 'underline',
    },
    current: {
        color: colours.font.bodyStrong,
        fontWeight: 700,
    },
    separator: {
        width: 14,
        height: 14,
        margin: '0 2px',
        verticalAlign: 'middle',
        opacity: 0.5,
    },
};

function BreadcrumbLink({href, onClick, children}) {
    const [hovered, setHovered] = useState(false);
    const linkStyle = hovered ? {...styles.link, ...styles.linkHover} : styles.link;

    return <a
        href={href}
        style={linkStyle}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
    >{children}</a>;
}

export default function Breadcrumbs() {
    const [crumbs, setCrumbs] = useState([]);
    const loggedIn = useSelector(isLoggedInSelector);
    const location = useLocation();

    // Detect current application from URL path
    const appSlugMatch = location.pathname.match(/^\/application\/([^/]+)/);
    const appSlug = appSlugMatch ? appSlugMatch[1] : null;
    const currentApp = useSelector(applicationBySlugSelector(appSlug));

    const portalCrumb = {label: 'Home', href: HOME_URL};

    useEffect(() => {
        const handleSet = (e) => {
            if (e.detail && Array.isArray(e.detail.crumbs)) {
                setCrumbs(e.detail.crumbs);
            }
        };
        const handleClear = () => setCrumbs([]);

        window.addEventListener('uniportal:set-breadcrumbs', handleSet);
        window.addEventListener('uniportal:clear-breadcrumbs', handleClear);
        return () => {
            window.removeEventListener('uniportal:set-breadcrumbs', handleSet);
            window.removeEventListener('uniportal:clear-breadcrumbs', handleClear);
        };
    }, []);

    useEffect(() => {
        setCrumbs([]);
    }, [appSlug, loggedIn]);

    if (!loggedIn) {
        return null;
    }

    // If no child app has dispatched crumbs but we're on an app route,
    // auto-generate a breadcrumb for the current application
    const effectiveCrumbs = crumbs.length > 0
        ? crumbs
        : (currentApp && currentApp.name ? [{ label: currentApp.name }] : []);

    const allCrumbs = [portalCrumb, ...effectiveCrumbs];

    const handleLinkClick = (e, crumb) => {
        if (!crumb.to) {
            return;
        }
        // Let the browser handle modifier/middle clicks (new tab/window).
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('uniportal:breadcrumb-navigate', {
            detail: {path: crumb.to}
        }));
    };

    return <nav aria-label="Breadcrumb">
        <ol style={styles.breadcrumbs}>
            {allCrumbs.map((c, idx) => {
                const isLast = idx === allCrumbs.length - 1;
                const sep = idx > 0 ? (
                    <img src="/images/icons/chevron-right.svg" alt="" aria-hidden="true" style={styles.separator} />
                ) : null;
                let content;
                if (isLast || (!c.to && !c.href)) {
                    content = <span style={styles.current}>{c.label}</span>;
                } else if (c.href) {
                    content = <BreadcrumbLink href={c.href}>{c.label}</BreadcrumbLink>;
                } else {
                    content = <BreadcrumbLink href={c.to} onClick={(e) => handleLinkClick(e, c)}>{c.label}</BreadcrumbLink>;
                }
                return (
                    <li key={`${c.label}-${idx}`} style={styles.crumb}>
                        {sep}
                        {content}
                    </li>
                );
            })}
        </ol>
    </nav>;
}
